import {
  Checkbox,
  Divider, FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import clsx from "clsx";
import i18next from "i18next";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import AttributeFormInputBloc from "../../../components/attributes/AttributeFormInputBloc";
import {
  AttributeType,
  ATTRIBUTE_MAX_LABEL_LENGTH,
  ATTRIBUTE_MAX_LIST_VALUE_LENGTH,
  DefaultSelectProps,
  REGEXP_SEMICOLON,
} from "../../../services/common/Constants";
import {
  AttributeCategories,
  getAttributeMaxLengthRule,
  hasAttributeListItemsToShow,
  hasAttributeListItemsToShowForCategory,
} from '../../../services/common/AttributeUtils';
import { getAccountAttributeByLabelAndBondType } from "../../../services/edge/AttributesService";
import { HighlightOff } from "@mui/icons-material";
import { isEmpty } from "../../../services/common/Utils";
import { Context } from "../../../states/Store";
import useStyles from "../../../style/js-style/containers/administration/attributes/AttributeFormStyle";
import EAButton from "../../../components/common/EAButton";
import {PrivilegeEnum, RoleBasedAccessControlContext} from "../../../services/common/RolesUtils";
import _ from "lodash";

const getFormDefaultValues = (attribute, bondType) => {
  return {
    label: attribute?.label,
    type: attribute?.type || "",
    bondType: attribute?.bondType || bondType || "",
    category: attribute?.category || "",
    isRequired: attribute?.isRequired || false,
    defaultValue: attribute?.defaultValue === "true" ? true : attribute?.defaultValue === "false" ? false : null,
    values: attribute?.values?.map((x) => {
      return {
        listItemId: x.id,
        value: x.value,
        defaultChoice: x.defaultChoice || null,
      };
    }) || [
      { listItemId: "", value: "" },
      { listItemId: "", value: "" },
    ],
  };
};

const AttributeForm = ({
                         formId,
                         attribute,
                         onTypeChange,
                         submitHandler,
                         formType = "create",
                         bondType,
                         onListLengthChange,
                         setIsValidateDisabled,
                         readOnly = false,
                       }) => {
  const { classes } = useStyles();
  const [state] = useContext(Context);
  const [isAttributeListItemsShown, setIsAttributeListItemsShown] = useState(
    attribute?.values?.length > 0
  );

  const getDefaultValuesIndex = (attribute) => {
    return attribute?.defaultValue === null ? null : attribute?.defaultValue === "true" ? 0 : 1;
  }

  const [isEditForm] = useState(formType === "edit");
  const [hoveredLine, setHoveredLine] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [defaultValuesIndex, setDefaultValuesIndex] = useState(getDefaultValuesIndex(attribute))
  const attributesTypes = Object.values(AttributeType);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);

  const {
    control,
    formState: { errors },
    formState,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    watch
  } = useForm({
    mode: "onChange",
    delayError: 500,
    defaultValues: useMemo(() => {
      return getFormDefaultValues(attribute, bondType);
    }, [attribute, bondType]),
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "values",
  });

  let selectedCategory = useWatch({ control, name: "category" });

  useEffect(() => {
    if (onListLengthChange) {
      onListLengthChange(fields.length < 2);
    }
  }, [fields, onListLengthChange]);

  useEffect(() => {
    setIsValidateDisabled(!_.isEmpty(formState.errors));
  }, [formState, getValues, setIsValidateDisabled]);

  const watchType = watch("type");

  // Initialise des valeurs pour le boolean
  useEffect(() => {
    if (watchType === AttributeType.BOOLEAN && bondType !== "dossier"){
      setIsAttributeListItemsShown(true);
      setValue("values", [
        { listItemId: "true", value: i18next.t("yes"), defaultChoice: attribute?.defaultValue === "true" },
        { listItemId: "false", value: i18next.t("no"), defaultChoice: attribute?.defaultValue === "false" },
      ])
    }
  }, [watchType, setValue, attribute, bondType]);

  const verifyLabelUniqueness = AwesomeDebouncePromise(async (value) => {
    const hasDifferentLabel = attribute?.label?.localeCompare(value, undefined, { sensitivity: 'accent' }) !== 0;
    const selectedBondType = AttributeCategories.find(x => x.value === selectedCategory)?.bondType || bondType;
    if (hasDifferentLabel && selectedBondType) {
      try {
        const result = await getAccountAttributeByLabelAndBondType(
          state.account.id,
          value,
          selectedBondType,
          null
        );
        return result.content?.id !== attribute?.id ? i18next.t("attributes.errors.labelUniqueness") : null;
      } catch (e) {
        return e?.message?.error?.[0].code === "NOT_FOUND" ? undefined : i18next.t("infos.INTERNAL_ERROR");
      }
    }
  }, 500);

  const verifyListvalueUniqueness = (value) => {
    return getValues("values").filter(
      (x) => x.value?.trim()?.toLowerCase() === value?.trim()?.toLowerCase()
    )?.length > 1
      ? i18next.t("attributes.errors.valueUniqueness")
      : null;
  };

  const verifyNotIncludeSemiColon = (value) => {
    return value && value.match(REGEXP_SEMICOLON)
    ? i18next.t("attributes.errors.nosemiclon")
    : null;
  }

  const validateLabelAttribute = (value) => {
    return verifyNotIncludeSemiColon(value) || verifyLabelUniqueness(value);
  }
  const validateListValueAttribute = (value) => {
    return verifyNotIncludeSemiColon(value) || verifyListvalueUniqueness(value);
  }

  const submitForm = (data) => {
    const values = data.values;
    if (isEmpty(errors)) {
      if([AttributeType.LIST_MULTIPLE, AttributeType.LIST_UNIQUE].includes(data.type) && values) {
        data.values = values?.map((x) => {
          return {
            id: x.listItemId,
            value: x.value,
            defaultChoice: x.defaultChoice || false,
          };
        })
      }

      // Si c'est un boolean, on définit le choix par défaut dans l'attribut
      if(data.type === AttributeType.BOOLEAN) {
        data = {
          ...data,
          defaultValue: defaultValuesIndex === null ? null : defaultValuesIndex === 0 ? "true" : "false",
          values: [ {id: "",value: "", defaultChoice: null}, {id: "",value: "", defaultChoice: null} ]
        }
      }

      submitHandler(data);
    }
  };

  const getInputRightBtn = (index) => {
    return (
      <div style={{position: "absolute", right: "0px", top: "-3px"}}>
        <Tooltip
          placement="top"
          title={i18next.t("delete")}
        >
          <IconButton
            aria-label={`remove attribute list field ${index + 1}`}
            onClick={() => remove(index)}
            className={classes.valuesIconBtnClosed}
            size="large"
          >
            <HighlightOff />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  function checkUniqueDefaultValue(e, index) {
    // Si une autre case a été coché, la décoche
    if (watchType !== AttributeType.LIST_MULTIPLE) {
      if(e.target.checked && getValues().values.filter(checkbox => checkbox.defaultChoice === true).length > 1) {
        getValues().values.forEach((checkbox, indexCB) => {
          if (checkbox.defaultChoice && indexCB !== index) {
            update(indexCB, { ...checkbox, defaultChoice: false });
          }
        })
      }
    }

    // set la valeur par défaut sélectionné
    if (e.target.checked){
      setDefaultValuesIndex(index)
    }else if(getValues().values.filter(checkbox => checkbox.defaultChoice === true).length === 0){
      setDefaultValuesIndex(null)
    }
  }

  const isGloballyDisabled = () => {
    if(!bondType) {
        return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ALL_PARTNER_ATTRIBUTE);
    } else {
      switch (bondType) {
        case "dossier":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ALL_DOSSIER_ATTRIBUTE);
        case "thirdparty":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ALL_THIRD_PARTY_ATTRIBUTE);
        case "context":
        case "context_third_party":
        case "context_third_party_demand":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ALL_CONTEXT_ATTRIBUTE);
        default:
          return true;
      }
    }
  };

  useEffect(() => {
    const selectedCategoryObj = availableCategories?.find(x => x.value === selectedCategory);
    if(selectedCategoryObj) {
      setValue("type", selectedCategoryObj.type[0]); // Handle later if there is multiple types for categorized attributes
      setValue("bondType", selectedCategoryObj.bondType);
      setIsAttributeListItemsShown(hasAttributeListItemsToShowForCategory(selectedCategoryObj));
    }
  }, [selectedCategory, availableCategories, setValue])

  useEffect(() => {
    if(!bondType) {
      setAvailableCategories(AttributeCategories.filter(
        category => _.find(state.subscriptions, { key: category.key })
      ))
    } else {
      setAvailableCategories([]);
    }
  }, [bondType, state.subscriptions])

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(submitForm)}
      className={classes.formContainer}
    >
      {!bondType && (
        <div className={classes.labelTypeBloc} style={{gap: "15px"}}>
          {/* Category */}
          <AttributeFormInputBloc
            label={`${i18next.t("category")} *`}
            className={classes.label}
            style={{flex: 1}}
          >
            <Controller
              control={control}
              name="category"
              rules={{
                validate: (value) => {
                  const isNotValid = value.length === 0;
                  if(isNotValid) {
                    const categoryErrMsg = i18next.t("infos.required");
                    setError('category', {category: 'required', message: categoryErrMsg})
                    return categoryErrMsg
                  } else {
                    clearErrors('category');
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  id="attribute-category-id"
                  variant="outlined"
                  select
                  disabled={isEditForm}
                  className={
                    isEditForm ? classes.disableInput : classes.enableInput
                  }
                  error={!!errors.category}
                  {...field}
                  onChange={(event) => {
                    setValue("category", event.target.value, { shouldValidate: true });
                  }}
                  SelectProps={{
                    ...DefaultSelectProps,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected.length === 0) {
                        return (
                          <span className={classes.placeHolder}>
                            {i18next.t(`attributeCategory.label`)}
                          </span>
                        );
                      }
                      return (
                        <span >
                          {i18next.t(`attributeCategory.${selected}`)}
                        </span>
                      );
                    }
                  }}
                >
                  <MenuItem disabled value="">
                    <span className={classes.placeHolder}>
                      {i18next.t(`attributeCategory.label`)}
                    </span>
                  </MenuItem>
                  {availableCategories?.map((category) => (
                    <MenuItem key={category.key} value={category.value}>
                      {i18next.t(`attributeCategory.${category.value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.category && (
              <FormHelperText className="Mui-error">
                {errors.category.message}
              </FormHelperText>
            )}
          </AttributeFormInputBloc>


          {/* Bond type for categorized attributes */}
          <AttributeFormInputBloc label={`${i18next.t("link")}`} style={{flex: 1}}>
            <Controller
              control={control}
              name="bondType"
              render={({ field }) => (
                <TextField
                  id="attribute-bond-type-id"
                  variant="outlined"
                  error={!!errors.bondType}
                  disabled
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { border: "none", }, }}
                  {...field}
                  value={
                    getValues("bondType") ?
                      i18next.t("attributes.linkedTo", {bondType: i18next.t(`attributes.${getValues("bondType")}`)}) :
                      i18next.t("attributes.link")
                  }
                />
              )}
            />
          </AttributeFormInputBloc>
          
        </div>
      )}
      
      <div className={classes.labelTypeBloc} style={{gap: "15px"}}>
        {/* Label */}
        <AttributeFormInputBloc label={`${i18next.t("label")} * `} style={{flex: 1}}>
          <Controller
            control={control}
            name="label"
            rules={{
              required: i18next.t("infos.required"),
              maxLength: getAttributeMaxLengthRule("theLabel", ATTRIBUTE_MAX_LABEL_LENGTH),
              validate: (value) => validateLabelAttribute(value) ,
            }}
            render={({ field }) => (
              <TextField
                id="attribute-label-id"
                variant="outlined"
                error={!!errors.label}
                disabled={isGloballyDisabled() || readOnly}
                placeholder={i18next.t("placeholderLabel")}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { border: "none", }, }}
                {...field}
              />
            )}
          />
          {errors.label && (
            <FormHelperText className="Mui-error">
              {errors.label.message}
            </FormHelperText>
          )}
        </AttributeFormInputBloc>

        {/* Type */}
        {/* Shown for classic attributes or categorized attributes that have a selected category */}
        {(selectedCategory || bondType) && (
          <AttributeFormInputBloc
            label={`${i18next.t("type")} *`}
            className={classes.label}
            style={{flex: 1}}
          >
            <Controller
              control={control}
              name="type"
              rules={{
                validate: (value) => {
                  const isNotValid = value.length === 0;
                  if(isNotValid) {
                    const typeErrMsg = i18next.t("infos.required");
                    setError('type', {type: 'required', message: typeErrMsg})
                    return typeErrMsg
                  } else {
                    clearErrors('type');
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  id="attribute-type-id"
                  variant="outlined"
                  select
                  disabled={isEditForm || selectedCategory}
                  className={
                    isEditForm ? classes.disableInput : classes.enableInput
                  }
                  error={!!errors.type}
                  {...field}
                  onChange={(event) => {
                    setValue("type", event.target.value, { shouldValidate: true });
                    setIsAttributeListItemsShown(
                      hasAttributeListItemsToShow(event.target.value, bondType)
                    );
                    onTypeChange &&
                      onTypeChange(hasAttributeListItemsToShow(event.target.value));
                  }}
                  SelectProps={{
                    ...DefaultSelectProps,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected.length === 0) {
                        return (
                          <span className={classes.placeHolder}>
                            {i18next.t(`valueType`)}
                          </span>
                        );
                      }
                      return (
                        <span >
                          {i18next.t(`attributeType.${selected}`)}
                        </span>
                      );
                    }
                  }}
                >
                  <MenuItem disabled value="">
                    <span className={classes.placeHolder}>
                      {i18next.t(`valueType`)}
                    </span>
                  </MenuItem>
                  {attributesTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {i18next.t(`attributeType.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {errors.type && (
              <FormHelperText className="Mui-error">
                {errors.type.message}
              </FormHelperText>
            )}
          </AttributeFormInputBloc>
        )}

        {bondType !== "dossier" && (
          <AttributeFormInputBloc
            label={`${i18next.t('required')}`}
            className={classes.label}
            style={{ flex: 0.3 }}
          >
            <Controller
              control={control}
              name="isRequired"
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      onChange={onChange}
                      sx={{ backgroundColor: "transparent !important", scale: 0.8 }}
                      disabled={
                        readOnly || (bondType === "dossier"
                          ? !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE)
                          : !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE))
                      }
                    />
                  }
                  sx={{
                    '&.MuiFormControlLabel-root': {
                      color: '#16161E',
                    },
                    '& .MuiTypography-root': {
                      fontSize: '14px !important',
                    },
                  }}
                  label={''}
                />
              )}
            />
          </AttributeFormInputBloc>
        )}

      </div>

      {/* Values */}
      {isAttributeListItemsShown && (
        <>
          <Divider />
          <div className={classes.valuesBloc}>
            <div
              className={clsx(
                classes.inputBlocContainer,
                classes.listValuesContainer
              )}
            >
              <div className="valuesLabel">
                <FormLabel className={classes.valuesLabel}>
                  {i18next.t("theValues")} *
                </FormLabel>
                {bondType !== "dossier" && (
                <div className={classes.optionalNotice}>
                  {watchType === AttributeType.LIST_MULTIPLE ? i18next.t('attributes.optionalNoticeMulti') : i18next.t('attributes.optionalNotice')}
                </div>
                )}
                {bondType !== "dossier" && (
                  <div className={classes.valuesLabelDefaultWrapper}>
                    <FormLabel className={classes.valuesLabelDefault}>
                      {i18next.t('byDefault')}
                    </FormLabel>
                  </div>
                )}
              </div>

              <Grid container>
                {fields.map((item, index) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      key={item.id}
                      className={classes.valuesWrapper}
                      onMouseEnter={() => setHoveredLine(index)}
                      onMouseLeave={() => setHoveredLine(null)}
                    >
                      <div className={watchType === AttributeType.BOOLEAN ? classes.listValueLineBoolean : classes.listValueLine}>
                        <FormGroup className={classes.listValueLineFromGroup}>
                          <div>
                            <Controller
                              name={`values.${index}.value`}
                              control={control}
                              rules={{
                                required: i18next.t("infos.required"),
                                maxLength: getAttributeMaxLengthRule(
                                  "theValue",
                                  ATTRIBUTE_MAX_LIST_VALUE_LENGTH
                                ),
                                validate: (value) =>
                                  validateListValueAttribute(value),
                              }}
                              render={({ field }) => (
                                <div className={watchType === AttributeType.BOOLEAN ? classes.inputTextValuesWrapperBoolean : classes.inputTextValuesWrapper}>
                                  <TextField
                                    onClick={(e) => e.stopPropagation()}
                                    className={watchType === AttributeType.BOOLEAN ? classes.inputTextValuesBoolean : classes.inputTextValues}
                                    variant="outlined"
                                    disabled={watchType === AttributeType.BOOLEAN || readOnly}
                                    fullWidth
                                    placeholder={i18next.t("placeholderValue")}
                                    error={
                                      !!errors.values &&
                                      !!errors.values[index]?.value
                                    }
                                    sx={{ "& .MuiOutlinedInput-root": { border: "none", }, }}
                                    {...field}
                                    onChange={(event) => {
                                      setValue(`values.${index}`, {
                                        listItemId: item.listItemId,
                                        value: event.target.value,
                                      });
                                      trigger("values");
                                    }}
                                  />
                                </div>
                              )}
                            />

                            {errors?.values?.[index]?.value && (
                              <FormHelperText className="Mui-error">
                                {errors.values[index]?.value?.message}
                              </FormHelperText>
                            )}
                          </div>
                          {hoveredLine === index && !readOnly &&
                            fields.length > 2 &&
                            getInputRightBtn(index)}

                          {/* Choix par défaut */}
                          {bondType !== "dossier" && (
                            <Controller
                              control={control}
                              name={`values.${index}.defaultChoice`}
                              render={({ field: { onChange, value } }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={value}
                                      onChange={(e) => {
                                        onChange(e);
                                        checkUniqueDefaultValue(e, index)
                                      }}
                                      sx={{ backgroundColor: "transparent !important", scale: 0.8 }}
                                      disabled={readOnly}
                                    />
                                  }
                                  sx={{
                                    '&.MuiFormControlLabel-root': {
                                      color: '#16161E',
                                    },
                                    '& .MuiTypography-root': {
                                      fontSize: '14px !important',
                                    },
                                  }}
                                  label={''}
                                />
                              )}
                            />
                          )}

                        </FormGroup>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Add value */}
              {watchType !== AttributeType.BOOLEAN && !readOnly &&
                <EAButton
                  borderless={true}
                  className={classes.addChoiceBtn}
                  onClick={() => append({ listItemId: '', value: '' })}
                  content={
                    <>
                      <AddIcon
                        style={{
                          width: '16px',
                          marginRight: '3px',
                          position: 'relative',
                          right: '3px',
                        }}
                      />
                      {i18next.t('attributes.addListItemBtnLabel')}
                    </>
                  }
                />
              }
            </div>
            <Divider className={classes.divider} />
          </div>
        </>
      )}
    </form>
  );
};

export default AttributeForm;
